import { useState } from 'react';
import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Flex,
  Grid,
  Popconfirm,
  Row,
  Skeleton,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import { useNavigate, useParams } from 'react-router';
import type { AdminPrice } from '@titularis/api-client';
import { rotuloDoPlano, useEstadoDeLista } from '@titularis/ui';
import { api } from '../../api';
import {
  condicaoDoPreco,
  emReais,
  formatarData,
  FORMA_LABEL,
} from '../../dominio';
import { AlteracoesDoProduto } from './AlteracoesDoProduto';
import { FormularioDePreco } from './FormularioDePreco';
import { FormularioDeProduto } from './FormularioDeProduto';
import { PedidosDoProduto } from './PedidosDoProduto';
import { SITUACAO_COR, SITUACAO_LABEL, situacaoDoProduto } from './situacao';

/**
 * Um produto por dentro: o que ele cobra, o que já vendeu e quem o mexeu.
 *
 * As três abas respondem três perguntas diferentes, e é por isso que são abas e
 * não uma página só: **quanto custa hoje** é decisão comercial, **o que já
 * vendeu** é suporte, e **quem mudou o quê** é auditoria - quase nunca se está
 * fazendo as três coisas ao mesmo tempo.
 *
 * A tabela de preços mostra também os desativados, e essa é a coluna que
 * responde "quando foi que isto virou R$ 849?" sem precisar da trilha: preço
 * aqui não se reescreve, se substitui.
 */
export function ProdutoPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const { token } = theme.useToken();
  const telas = Grid.useBreakpoint();
  const compacto = !telas.md;
  const { ler, filtrar } = useEstadoDeLista();

  const [editando, setEditando] = useState(false);
  const [precificando, setPrecificando] = useState<{ preco?: AdminPrice } | null>(null);

  const produto = useQuery({
    queryKey: ['produto', id],
    queryFn: () => api.getAdminProduct(id),
  });

  const invalidar = async () => {
    await queryClient.invalidateQueries({ queryKey: ['produto', id] });
    await queryClient.invalidateQueries({ queryKey: ['produtos'] });
  };

  const alternarPreco = useMutation({
    mutationFn: ({ preco, active }: { preco: AdminPrice; active: boolean }) =>
      api.togglePrice(preco.id, active),
    onSuccess: async (_, { active }) => {
      message.success(active ? 'Preço de volta à venda.' : 'Preço fora de venda.');
      await invalidar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const excluirPreco = useMutation({
    mutationFn: (preco: AdminPrice) => api.deletePrice(preco.id),
    onSuccess: async () => {
      message.success('Preço apagado.');
      await invalidar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const alternarProduto = useMutation({
    mutationFn: (active: boolean) => api.updateProduct(id, { active }),
    onSuccess: async (salvo) => {
      message.success(salvo.active ? 'Produto à venda.' : 'Produto fora de venda.');
      await invalidar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const excluirProduto = useMutation({
    mutationFn: () => api.deleteProduct(id),
    onSuccess: async () => {
      message.success('Produto apagado.');
      await queryClient.invalidateQueries({ queryKey: ['produtos'] });
      void navigate('/produtos');
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  if (produto.isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;

  if (produto.error || !produto.data) {
    return (
      <Flex vertical gap={16}>
        <Alert
          type="error"
          showIcon
          message={(produto.error as Error | undefined)?.message ?? 'Produto não encontrado.'}
        />
        <Button icon={<ArrowLeftOutlined />} onClick={() => void navigate('/produtos')}>
          Voltar aos produtos
        </Button>
      </Flex>
    );
  }

  const dados = produto.data;
  const situacao = situacaoDoProduto(dados);

  /*
   * Desligar o produto é a única ação daqui que o aluno sente na hora: ele some
   * da página de planos. Confirmar antes evita o clique de passagem num
   * interruptor que fica ao lado do nome.
   */
  const confirmarDesligar = () => {
    modal.confirm({
      title: 'Tirar de venda?',
      content: `"${dados.name}" sai da página de planos imediatamente. Quem já comprou continua com o acesso, e o histórico de vendas fica de pé.`,
      okText: 'Tirar de venda',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: () => alternarProduto.mutateAsync(false),
    });
  };

  const colunasDePreco = [
    {
      /*
       * O plano vem antes da forma porque é ele que distingue as linhas: com
       * mensal, semestral e anual à venda, três linhas dizem "Cartão" e só a
       * duração as separa.
       */
      title: 'Plano',
      key: 'plano',
      render: (_: unknown, preco: AdminPrice) => (
        <Flex align="center" gap={6}>
          <Typography.Text strong={preco.active}>
            {rotuloDoPlano(preco.accessMonths)}
          </Typography.Text>
          {preco.recurring && (
            <Tooltip title="O provedor cobra a cada ciclo até o aluno cancelar.">
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                renova
              </Tag>
            </Tooltip>
          )}
        </Flex>
      ),
    },
    {
      title: 'Forma',
      key: 'forma',
      render: (_: unknown, preco: AdminPrice) => FORMA_LABEL[preco.kind] ?? preco.kind,
    },
    {
      title: 'Valor',
      key: 'valor',
      render: (_: unknown, preco: AdminPrice) => (
        <Flex vertical gap={0}>
          <Typography.Text strong={preco.active}>{emReais(preco.amountCents)}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {condicaoDoPreco(preco.amountCents, preco.maxInstallments)}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'Situação',
      key: 'situacao',
      render: (_: unknown, preco: AdminPrice) =>
        preco.active ? (
          <Tag color="green" style={{ marginInlineEnd: 0 }}>
            À venda
          </Tag>
        ) : (
          <Tag style={{ marginInlineEnd: 0 }}>Substituído</Tag>
        ),
    },
    {
      /*
       * "Pedidos", e não "Vendas": aqui entra todo pedido que nasceu deste
       * preço, inclusive o que expirou sem ser pago. Chamá-lo de venda punha
       * "14" ao lado do cartão de "Vendas pagas: 4" na mesma tela.
       */
      title: 'Pedidos',
      key: 'pedidos',
      align: 'right' as const,
      render: (_: unknown, preco: AdminPrice) => (
        <Tooltip title="Pedidos abertos com este preço, pagos ou não.">
          <span>{preco.pedidos}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Desde',
      key: 'createdAt',
      render: (_: unknown, preco: AdminPrice) => formatarData(preco.createdAt),
    },
    {
      title: '',
      key: 'acoes',
      width: 160,
      render: (_: unknown, preco: AdminPrice) => (
        <Flex gap={4} justify="flex-end">
          {/*
            **Mudar a partir da linha, e não só pelo botão do topo.** Enquanto
            havia um preço por forma de pagamento, abrir o formulário em branco
            e escolher "Pix" bastava para cair no preço certo. Com três planos
            à venda, quem quisesse mexer no semestral teria de redigitar forma,
            duração e recorrência sem errar nenhuma - e errar qualquer uma não
            dá erro: cria um quarto plano, calada.
          */}
          {preco.active && (
            <Button size="small" onClick={() => setPrecificando({ preco })}>
              Mudar
            </Button>
          )}

          {preco.active ? (
            <Button
              size="small"
              onClick={() => alternarPreco.mutate({ preco, active: false })}
              loading={alternarPreco.isPending && alternarPreco.variables?.preco.id === preco.id}
            >
              Tirar de venda
            </Button>
          ) : (
            <Tooltip title={`Volta a vender por ${emReais(preco.amountCents)} e tira o preço ${rotuloDoPlano(preco.accessMonths).toLowerCase()} de ${FORMA_LABEL[preco.kind] ?? preco.kind} que está no ar.`}>
              <Button
                size="small"
                onClick={() => alternarPreco.mutate({ preco, active: true })}
                loading={alternarPreco.isPending && alternarPreco.variables?.preco.id === preco.id}
              >
                Voltar a vender
              </Button>
            </Tooltip>
          )}

          {/*
            Apagar só existe para o erro de digitação recém-cometido: preço já
            cobrado é a condição em que a venda aconteceu, e a rota o recusa.
          */}
          {preco.pedidos === 0 && (
            <Popconfirm
              title="Apagar este preço?"
              description="Ele nunca foi cobrado, então some sem deixar buraco no histórico."
              okText="Apagar"
              okButtonProps={{ danger: true }}
              cancelText="Cancelar"
              onConfirm={() => excluirPreco.mutate(preco)}
            >
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Flex>
      ),
    },
  ];

  return (
    <Flex vertical gap={16}>
      <div>
        <Button
          type="link"
          size="small"
          icon={<ArrowLeftOutlined />}
          style={{ paddingInline: 0 }}
          onClick={() => void navigate('/produtos')}
        >
          Produtos
        </Button>

        <Flex justify="space-between" align="flex-start" wrap gap={12}>
          <Flex vertical gap={4} style={{ minWidth: 0 }}>
            <Flex align="center" gap={8} wrap>
              <Typography.Title level={compacto ? 4 : 3} style={{ margin: 0 }}>
                {dados.name}
              </Typography.Title>
              <Tag color={SITUACAO_COR[situacao]}>{SITUACAO_LABEL[situacao]}</Tag>
            </Flex>
            <Typography.Text type="secondary">
              <code>{dados.key}</code>
            </Typography.Text>
          </Flex>

          <Flex gap={8} align="center" wrap>
            <Flex align="center" gap={6}>
              <Switch
                checked={dados.active}
                loading={alternarProduto.isPending}
                onChange={(ligado) =>
                  ligado ? alternarProduto.mutate(true) : confirmarDesligar()
                }
              />
              <Typography.Text type="secondary">À venda</Typography.Text>
            </Flex>
            <Button icon={<EditOutlined />} onClick={() => setEditando(true)}>
              Editar
            </Button>
            <Popconfirm
              title="Apagar o produto?"
              description={
                dados.vendas.pagos + dados.vendas.abertos > 0
                  ? 'Ele tem pedidos no histórico: a rota vai recusar. Use "tirar de venda".'
                  : 'Ele nunca vendeu, então some sem deixar buraco no histórico.'
              }
              okText="Apagar"
              okButtonProps={{ danger: true }}
              cancelText="Cancelar"
              onConfirm={() => excluirProduto.mutate()}
            >
              <Button danger icon={<DeleteOutlined />} loading={excluirProduto.isPending}>
                Apagar
              </Button>
            </Popconfirm>
          </Flex>
        </Flex>
      </div>

      {situacao === 'sem_preco' && (
        <Alert
          type="warning"
          showIcon
          message="Ligado, mas invisível."
          description="A vitrine só mostra produto com preço ativo, então hoje ninguém consegue comprar este. Defina um preço na aba abaixo."
        />
      )}

      <Row gutter={[12, 12]}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Receita" value={emReais(dados.vendas.receitaCents)} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Vendas pagas" value={dados.vendas.pagos} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Em aberto" value={dados.vendas.abertos} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Devolvidas"
              value={dados.vendas.desfeitos}
              /*
               * Estorno e contestação tiram o acesso: não é um número neutro.
               * `styles.content`, e não `valueStyle`, que o Antd v6 depreciou.
               */
              styles={
                dados.vendas.desfeitos > 0
                  ? { content: { color: token.colorError } }
                  : undefined
              }
            />
          </Card>
        </Col>
      </Row>

      <Card size="small">
        <Descriptions column={compacto ? 1 : 2} size="small">
          <Descriptions.Item label="Descrição" span={compacto ? 1 : 2}>
            {dados.description ?? <Typography.Text type="secondary">sem descrição</Typography.Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Libera" span={compacto ? 1 : 2}>
            <Flex gap={6} wrap>
              {dados.assinantes.map((direito) => (
                <Tooltip
                  key={direito.chave}
                  title={`${direito.quantos} pessoa(s) com este acesso vigente hoje, de qualquer origem.`}
                >
                  {/* O rótulo vem do servidor: o nome de uma lei só existe depois
                      de uma ida ao acervo (D-45). */}
                  <Tag style={{ marginInlineEnd: 0 }}>
                    {dados.rotulos[direito.chave] ?? direito.chave} · {direito.quantos}
                  </Tag>
                </Tooltip>
              ))}
            </Flex>
          </Descriptions.Item>
          <Descriptions.Item label="Criado">{formatarData(dados.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="Última venda">
            {dados.vendas.ultimaVendaEm ? formatarData(dados.vendas.ultimaVendaEm) : '—'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Tabs
        activeKey={ler.texto('aba') ?? 'precos'}
        onChange={(chave) => filtrar({ aba: chave === 'precos' ? undefined : chave })}
        items={[
          {
            key: 'precos',
            label: 'Preços',
            children: (
              <Flex vertical gap={12}>
                <Flex justify="space-between" align="center" wrap gap={8}>
                  <Typography.Text type="secondary">
                    Mudar um preço não reescreve o anterior: ele fica desativado, explicando as
                    vendas que aconteceram por aquele valor.
                  </Typography.Text>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setPrecificando({})}
                  >
                    Definir preço
                  </Button>
                </Flex>

                <Card styles={{ body: { padding: 0 } }}>
                  <Table
                    rowKey="id"
                    size={compacto ? 'small' : undefined}
                    dataSource={dados.precos}
                    columns={colunasDePreco}
                    pagination={false}
                    locale={{
                      emptyText: (
                        <Empty description="Sem preço ainda. Enquanto não houver, o produto não aparece na vitrine." />
                      ),
                    }}
                    scroll={{
                      /*
                       * Em número e não `true`: a coluna do plano entrou e as
                       * ações ganharam o botão de mudar, então 760 passou a
                       * cortar a última coluna.
                       */
                      x: 940,
                    }}
                  />
                </Card>
              </Flex>
            ),
          },
          {
            key: 'vendas',
            label: 'Vendas',
            children: <PedidosDoProduto productId={id} />,
          },
          {
            key: 'alteracoes',
            label: 'Alterações',
            children: <AlteracoesDoProduto productId={id} />,
          },
        ]}
      />

      <FormularioDeProduto
        aberto={editando}
        produto={dados}
        aoFechar={() => setEditando(false)}
      />

      {precificando && (
        <FormularioDePreco
          aberto
          produto={dados}
          precoInicial={precificando.preco}
          aoFechar={() => setPrecificando(null)}
        />
      )}
    </Flex>
  );
}
